import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const necklaceSizes = ['35 cm', '40 cm', '45 cm', '50 cm'];
const ringBraceletSizes = [
  '5 (15.6 mm)', '6 (16.45 mm)', '7 (17.3 mm)', '8 (18.2 mm)',
  '9 (19.0 mm)', '10 (19.8 mm)', '11 (20.6 mm)', '12 (21.4 mm)', '13 (22.2 mm)', 'Free Size'
];

const AddProduct = () => {
  const [form, setForm] = useState({
    productId: '',
    name: '',
    description: '',
    weight: '',
    expense: '',
    benefit: '',
    productType: [],
    stock: ''
  });

  const [photos, setPhotos] = useState([]);
  const [goldRateIQD, setGoldRateIQD] = useState(0);
  const [usdToIqdRate, setUsdToIqdRate] = useState(0);
  const [price, setPrice] = useState(0);
  const [message, setMessage] = useState('');
  const [sizes, setSizes] = useState([]);

  const productTypeOptions = [
    { value: 'Necklace', label: 'Necklace' },
    { value: 'Bracelet', label: 'Bracelet' },
    { value: 'Ring', label: 'Ring' },
    { value: 'Earring', label: 'Earring' },
  ];

  const getSizeOptions = () => {
    if (form.productType.includes('Necklace')) return necklaceSizes;
    if (form.productType.includes('Ring') || form.productType.includes('Bracelet')) return ringBraceletSizes;
    return [];
  };

  useEffect(() => {
    axios.get('/api/settings')
      .then(res => {
        setGoldRateIQD(res.data.goldRateIQD);
        setUsdToIqdRate(res.data.usdToIqdRate);
      });
  }, []);

  useEffect(() => {
    const { weight, expense, benefit } = form;
    if (weight && expense && benefit && goldRateIQD && usdToIqdRate) {
      const calculated = (((weight * goldRateIQD) + parseFloat(expense)) / usdToIqdRate) + parseFloat(benefit);
      setPrice(calculated.toFixed(2));
    }
  }, [form, goldRateIQD, usdToIqdRate]);

  const handleTypeChange = (selectedOptions) => {
    const selectedTypes = selectedOptions.map(option => option.value);
    setForm({ ...form, productType: selectedTypes });
  
    if (selectedTypes.includes('Earring')) {
      // Earrings have no size — add default size entry
      setSizes([{ size: 'Default', stock: 1 }]);
    } else {
      setSizes([]); // Reset sizes for other types
    }
  };
  

  const handleFileChange = (e) => {
    setPhotos(e.target.files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    for (let key in form) {
      if (key !== 'productType') {
        formData.append(key, form[key]);
      }
    }

    form.productType.forEach((type) => {
      formData.append('productType', type);
    });

    for (let file of photos) {
      formData.append('photos', file);
    }

    if (sizes.length > 0) {
      formData.append('sizes', JSON.stringify(sizes));
    }
    
    try {
      await axios.post('/api/products/create', formData);
      setMessage('Product uploaded successfully');
      setForm({ productId: '', name: '', description: '', weight: '', expense: '', benefit: '', productType: [], stock: '' });
      setPhotos([]);
      setSizes([]);
    } catch (err) {
      console.error(err);
      setMessage('Failed to upload product');
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white pt-12 p-6 rounded shadow mt-6">
      <h2 className="text-2xl font-bold mb-4">Add Ready Product</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          name="productId"
          value={form.productId}
          onChange={e => setForm({ ...form, productId: e.target.value })}
          placeholder="Product ID"
          className="border p-2 rounded w-full"
          required
        />
        <input
          name="name"
          value={form.name}
          onChange={e => setForm({ ...form, [e.target.name]: e.target.value })}
          placeholder="Product Name"
          className="w-full border p-2 rounded"
          required
        />
        <ReactQuill
  value={form.description}
  onChange={(value) => setForm({ ...form, description: value })}
  placeholder="Enter product description..."
  theme="snow"
  className="mb-4"
/>
        <input
          name="weight"
          type="number"
          value={form.weight}
          onChange={e => setForm({ ...form, [e.target.name]: e.target.value })}
          placeholder="Weight (grams)"
          className="w-full border p-2 rounded"
          required
        />
        <input
          name="expense"
          type="number"
          value={form.expense}
          onChange={e => setForm({ ...form, [e.target.name]: e.target.value })}
          placeholder="Expense (IQD)"
          className="w-full border p-2 rounded"
          required
        />
        <input
          name="benefit"
          type="number"
          value={form.benefit}
          onChange={e => setForm({ ...form, [e.target.name]: e.target.value })}
          placeholder="Benefit (USD)"
          className="w-full border p-2 rounded"
          required
        />

        {/* Hide old stock field when size options are shown */}
        {getSizeOptions().length === 0 && (
          <input
            name="stock"
            type="number"
            value={form.stock}
            onChange={e => setForm({ ...form, stock: e.target.value })}
            placeholder="Stock"
            className="w-full border p-2 rounded"
            required
          />
        )}

        <Select
          isMulti
          name="productType"
          options={productTypeOptions}
          className="w-full"
          value={productTypeOptions.filter(option => form.productType.includes(option.value))}
          onChange={handleTypeChange}
          placeholder="Select product types"
        />

        {/* Size & Stock Inputs */}
        {getSizeOptions().length > 0 && (
          <div>
            <h4 className="font-semibold mt-4 mb-2">Select Sizes & Stock</h4>
            {getSizeOptions().map((size, idx) => (
              <div key={idx} className="flex items-center gap-4 mb-2">
                <span className="text-sm w-32">{size}</span>
                <input
                  type="number"
                  min="0"
                  placeholder="Stock"
                  className="border p-2 rounded w-full"
                  value={sizes.find(s => s.size === size)?.stock || ''}
                  onChange={(e) => {
                    const updated = [...sizes];
                    const existing = updated.find(s => s.size === size);
                    if (existing) {
                      existing.stock = Number(e.target.value);
                    } else {
                      updated.push({ size, stock: Number(e.target.value) });
                    }
                    setSizes(updated);
                  }}
                />
              </div>
            ))}
          </div>
        )}

        <input
          type="file"
          multiple
          onChange={handleFileChange}
          className="block"
        />

        <p className="text-sm text-gray-700">
          Auto-Calculated Price: <strong>${price}</strong>
        </p>

        <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
          Upload Product
        </button>
        {message && <p className="text-blue-600">{message}</p>}
      </form>
    </div>
  );
};

export default AddProduct;
